/**
 * Carbon text input: the field frame (label, helper or error) around a native
 * <input>. 40px by default; `size` sm 32px or lg 48px. Blocking validation
 * adds the Coral outline and the octagon at the trailing edge, and the error
 * text replaces the helper. Every native input prop passes through.
 */
import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react'
import clsx from 'clsx'
import { FormField } from './FormField'
import { FIELD_BASE, FIELD_HEIGHT, FIELD_INVALID, fieldIds } from './fieldStyles'
import { IconSeverityCrit } from './icons'
import type { FieldSize } from './types'

/** The frame props every field primitive shares. */
export interface FieldChromeProps {
  label: ReactNode
  helper?: ReactNode
  /** Blocking validation only (Coral). */
  error?: ReactNode
  hideLabel?: boolean
  size?: FieldSize
}

export interface TextInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'>, FieldChromeProps {
  /** Class for the outer frame; `className` styles the input itself. */
  wrapperClassName?: string
}

// Native date and number inputs draw their own trailing controls, so the error glyph stays out of their way.
const NO_GLYPH = new Set(['date', 'number', 'time', 'datetime-local', 'month', 'week'])

export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(function TextInput(
  { label, helper, error, hideLabel, size = 'md', id: idProp, className, wrapperClassName, type = 'text', disabled, ...rest },
  ref,
) {
  const autoId = useId()
  const id = idProp ?? autoId
  const { describedBy } = fieldIds(id, helper, error)
  const glyph = !!error && !NO_GLYPH.has(type)
  return (
    <FormField id={id} label={label} helper={helper} error={error} hideLabel={hideLabel} disabled={disabled} className={wrapperClassName}>
      <div className="relative">
        <input
          ref={ref}
          id={id}
          type={type}
          disabled={disabled}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          className={clsx(FIELD_BASE, FIELD_HEIGHT[size], error && FIELD_INVALID, glyph && 'pr-10', className)}
          {...rest}
        />
        {glyph ? (
          <IconSeverityCrit className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-sev-crit" />
        ) : null}
      </div>
    </FormField>
  )
})

export default TextInput
